import { Globe, Plus } from 'lucide-react';
import { useState } from 'react';
import { setBotInboxes } from '../../api/endpoints';
import type { BotProfile, Inbox } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { MultiSelect } from '../../components/MultiSelect';
import { Spinner } from '../../components/Spinner';
import { useConsoleNav } from '../../lib/consoleNav';
import type { ChatSection } from './botSetup';

/**
 * Every inbox the assistant answers, website chats included.
 *
 * The website half of this used to be the whole tab — the snippet appeared
 * here, and only after the assignment was saved. It is Install's now, where
 * the chat is created, pointed at the assistant and put on a page in one
 * place; what is left is the question for everyone else: should it also
 * answer email, WhatsApp, SMS.
 *
 * Connecting one of those is the Connect screen's job (OAuth for most of
 * them), which framed is a new signed-in tab rather than a route in place.
 */
export function ChannelsSection({
  profile,
  inboxes,
  onProfile,
  onGo,
}: {
  profile: BotProfile;
  inboxes: Inbox[];
  onProfile: (fresh: BotProfile) => void;
  onGo: (section: ChatSection) => void;
}) {
  const openConsole = useConsoleNav();
  const [selected, setSelected] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const current = selected ?? profile.inboxIds;

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const result = await setBotInboxes(current);
      setSelected(null);
      setSaved(true);
      onProfile({ ...profile, inboxIds: result.inboxIds });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save where it answers.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Where it answers</h2>
      <p className="manage-card-sub">
        The assistant only answers conversations in these inboxes. Everything else reaches your team untouched.
      </p>
      {error && <ErrorNote error={error} />}
      {saved && <Note tone="ok">Saved.</Note>}
      <MultiSelect
        options={inboxes.map((inbox) => ({
          value: inbox.id,
          name: inbox.name,
          sub: inbox.channelType === 'website' ? 'website chat' : inbox.channelType,
        }))}
        selected={current}
        onChange={(next) => {
          setSelected(next);
          setSaved(false);
        }}
        emptyLabel="No inboxes yet."
      />
      <div className="bot-wizard-links">
        <button type="button" className="btn btn-secondary" onClick={() => onGo('install')}>
          <Globe size={14} />
          Add a website chat
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => openConsole('/connect')}>
          <Plus size={14} />
          Connect another channel
        </button>
      </div>
      <div className="manage-card-actions">
        <button className="btn btn-primary" onClick={() => void save()} disabled={busy || selected === null}>
          {busy && <Spinner />}
          Save
        </button>
      </div>
    </section>
  );
}
